/**
 * 입력창 (DESIGN_SPEC 6장). 하단 고정, 키보드를 따라 올라갑니다(화면이 dvh 컬럼이라 저절로).
 *
 * - modes 를 주면 하루/고민 세그먼트가 붙습니다(상회게시판). 1:1 은 세그먼트 없이 씁니다
 * - showPhotoButton 은 하루 모드에서만 켭니다. 사진은 1장까지 (DEV_SPEC 4.2)
 * - 비어 있으면 전송이 40% 투명이고 눌리지 않습니다
 * - generating 이면 전송 자리에 중단 버튼이 섭니다 (DEV_SPEC 4.4)
 *
 * Enter 는 줄바꿈, 보내기는 전송 버튼을 눌렀을 때만(사용자 요청 2026-10-15, 별채 입력창과 같게).
 */
import { useLayoutEffect, useRef } from 'react';
import { ArrowUp, ImagePlus } from 'lucide-react';
import { SegmentedControl } from './SegmentedControl.jsx';

const MAX_LINES = 5;
const LINE_HEIGHT_PX = 26;
const VERTICAL_PADDING_PX = 16;

export function Composer({
    value,
    onChange,
    onSubmit,
    placeholder,
    modes = null,
    mode = null,
    onModeChange = null,
    showPhotoButton = false,
    onPhotoClick = null,
    generating = false,
    onStop = null,
    // 답이 나오는 동안 입력칸 자리 글자 (시안 03 C1: 「○○가 답하는 중」)
    generatingPlaceholder = null,
    disabled = false,
    // 보내는 중. 입력칸은 그대로 두고(모바일 키보드가 닫히지 않게) 보내기만 막습니다
    busy = false,
    maxLength = 2000,
    inputRef = null,
}) {
    const localRef = useRef(null);
    // 부르는 쪽이 포커스를 옮길 수 있게 ref 를 받습니다 (게시판 체크인의 「오늘 얘기하기」)
    const textareaRef = inputRef ?? localRef;
    const canSend = !disabled && !busy && !generating && value.trim().length > 0;

    // 입력에 맞춰 높이를 늘립니다. 다섯 줄을 넘으면 그 안에서 스크롤합니다.
    useLayoutEffect(() => {
        const textarea = textareaRef.current;
        if (!textarea) {
            return;
        }
        textarea.style.height = 'auto';
        // border-box 라 scrollHeight 에 테두리를 더해야 2px 넘쳐 스크롤바가 서지 않습니다.
        const borderHeight = textarea.offsetHeight - textarea.clientHeight;
        const contentHeight = textarea.scrollHeight + borderHeight;
        const maxHeight = LINE_HEIGHT_PX * MAX_LINES + VERTICAL_PADDING_PX + borderHeight;
        textarea.style.height = `${Math.min(contentHeight, maxHeight)}px`;
        textarea.style.overflowY = contentHeight > maxHeight ? 'auto' : 'hidden';
    }, [value]);

    function submit() {
        if (!canSend) {
            return;
        }
        onSubmit(value.trim());
    }

    return (
        <form
            className="flex shrink-0 flex-col gap-2 border-t border-hairline bg-bg px-4 pt-2 pb-[max(10px,env(safe-area-inset-bottom))]"
            onSubmit={(event) => {
                event.preventDefault();
                submit();
            }}
        >
            {modes ? (
                <SegmentedControl options={modes} value={mode} onChange={onModeChange} ariaLabel="보낼 종류" />
            ) : null}
            <div className="flex items-end gap-2">
                {showPhotoButton ? (
                    <button
                        type="button"
                        aria-label="사진 첨부"
                        onClick={onPhotoClick}
                        className="flex size-touch shrink-0 items-center justify-center rounded-card text-text-secondary"
                    >
                        <ImagePlus size={22} strokeWidth={1.6} aria-hidden="true" />
                    </button>
                ) : null}
                <textarea
                    ref={textareaRef}
                    rows={1}
                    value={value}
                    maxLength={maxLength}
                    disabled={disabled || (generating && Boolean(generatingPlaceholder))}
                    aria-label={placeholder}
                    placeholder={generating && generatingPlaceholder ? generatingPlaceholder : placeholder}
                    onChange={(event) => {
                        onChange(event.target.value);
                    }}
                    className="min-h-touch min-w-0 flex-1 resize-none rounded-card border border-hairline bg-surface px-3.5 py-2 text-body text-text-primary placeholder:text-text-tertiary disabled:opacity-60"
                />
                {generating ? (
                    <button
                        type="button"
                        aria-label="답 멈추기"
                        onClick={onStop}
                        className="flex size-touch shrink-0 items-center justify-center rounded-full border border-brand bg-surface text-brand"
                    >
                        <span aria-hidden="true" className="size-3.5 rounded-[3px] bg-brand" />
                    </button>
                ) : (
                    <button
                        type="submit"
                        aria-label="보내기"
                        disabled={!canSend}
                        className="flex size-touch shrink-0 items-center justify-center rounded-full bg-brand text-on-brand disabled:opacity-40"
                    >
                        <ArrowUp size={20} strokeWidth={2} aria-hidden="true" />
                    </button>
                )}
            </div>
        </form>
    );
}
